import { Navigate } from "@tanstack/react-router";
import {
  Component,
  type GetDerivedStateFromError,
  type PropsWithChildren,
} from "react";

import { SearchDataMissingError } from "./SearchDataMissingError";

type Props = PropsWithChildren;

type State = {
  hasError: boolean;
};

/**
 * Error boundary component that catches SearchDataMissingError instances and redirects to the home page.
 *
 * This component wraps child components and catches any SearchDataMissingError that occurs during rendering.
 * When such an error is caught, it redirects the user to the home page with flights category selected.
 * All other errors are re-thrown and not handled by this boundary.
 *
 * Helps with cases of directly entering routes that require search data.
 */
export class SearchDataMissingErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError: GetDerivedStateFromError<Props, State> = (
    error,
  ) => {
    if (error instanceof SearchDataMissingError) {
      return { hasError: true };
    }

    throw error;
  };

  render() {
    if (this.state.hasError) {
      return <Navigate to="/" replace />;
    }

    return this.props.children;
  }
}
